import { useMutation } from '@tanstack/react-query'
import { getJwt } from '@/shared/lib/betterAuth/get-jwt'
import { queryClient } from '@/shared/lib/tanstack'
import { CLAIMS_QUERY_KEYS, USER_QUERY_KEY } from '@/shared/constants/cache'
import {
  createRentersClaim,
  updateUserProfile,
  uploadRentersClaimFile,
} from '@/services/renters-claims.services'
import type { UpdateUserProfileBody } from '@/services/renters-claims.services'

export type CreateRentersClaimInput = {
  policyId: string
  dateOfLoss: string
  description: string
  phone: string
  files: File[]
  /** Sent only when the user changed their phone number */
  profileUpdate?: UpdateUserProfileBody
}

export function useCreateRentersClaim() {
  return useMutation({
    mutationFn: async ({
      policyId,
      dateOfLoss,
      description,
      phone,
      files,
      profileUpdate,
    }: CreateRentersClaimInput) => {
      // Unlike mobile, a failed upload aborts the submission so the claim
      // is never created without its attachments
      const fileIds = await Promise.all(files.map(uploadRentersClaimFile))

      const claim = await createRentersClaim({
        customer_description: description,
        date_of_loss: dateOfLoss,
        policy_id_ci: policyId,
        latest_phone_number: phone,
        attachments: {
          create: fileIds.map((id) => ({ directus_files_id: id })),
        },
      })

      if (profileUpdate) {
        // The claim already exists at this point, so a profile failure must not
        // surface as a submit error (the user would resubmit a duplicate claim)
        try {
          const token = await getJwt()
          if (token) await updateUserProfile(token, profileUpdate)
        } catch (error) {
          console.error(error)
        }
      }

      return claim
    },
    onSuccess: (_, { profileUpdate }) => {
      queryClient.invalidateQueries({ queryKey: CLAIMS_QUERY_KEYS.renters })
      if (profileUpdate) {
        queryClient.invalidateQueries({ queryKey: USER_QUERY_KEY })
      }
    },
    onError: console.error,
  })
}
